import _ from "lodash";
import { HttpService } from "@/core";
import { ExportReq, ExportResp } from "@/core/interface";
import { generateUuid } from "@/utils";
import {
  QuotePageReq,
  QuotePageResp,
  QuoteDetailResp,
  QuoteReq,
  QuantityReq,
  QuantityDetailResp,
  QuantityImportResp,
  MaterialReq,
  MaterialDetailResp,
  MaterialImportResp,
  CategoryListResp,
  CategoryRecord,
} from "./quote.interface";

export class QuoteService {
  static usePageApi() {
    const api = (params: QuotePageReq) =>
      HttpService.axios({
        method: "GET",
        url: "/bid_offer/page",
        params,
      });
    return HttpService.usePagination<QuotePageResp>(api);
  }

  static useExportApi() {
    const api = (params: ExportReq) =>
      HttpService.axios({
        method: "GET",
        url: "/bid_offer/export",
        params,
      });
    return HttpService.useRequest<ExportResp>(api, { isOutError: true });
  }

  static useImportCategoryApi() {
    const api = (url: string) =>
      HttpService.axios({
        method: "POST",
        url: `/bid_offer/import/category`,
        data: { url },
      });
    return HttpService.useRequest<CategoryListResp>(api);
  }

  static useCreateApi() {
    const api = (data: QuoteReq) =>
      HttpService.axios({
        method: "POST",
        url: "/bid_offer/all",
        data,
      });
    return HttpService.useRequest<{ id: string }>(api);
  }

  static useDetailApi() {
    const api = (id: string) =>
      HttpService.axios({
        method: "GET",
        url: `/bid_offer/${id}`,
      });
    return HttpService.useRequest<QuoteDetailResp>(api);
  }

  static useEditApi() {
    const api = (id: string, data: QuoteReq) =>
      HttpService.axios({
        method: "PUT",
        url: `/bid_offer/${id}`,
        data,
      });
    return HttpService.useRequest<{ id: string }>(api);
  }

  static useQuantityCreateApi() {
    const api = (quoteId: string, data: QuantityReq) =>
      HttpService.axios({
        method: "POST",
        url: `/bid_offer/${quoteId}/quantities`,
        data,
      });
    return HttpService.useRequest<void>(api);
  }

  static useQuantityDetailApi() {
    const api = (quoteId: string) =>
      HttpService.axios({
        method: "GET",
        url: `/bid_offer/${quoteId}/quantities`,
      });
    return HttpService.useRequest<QuantityDetailResp>(api);
  }

  static useQuantityExportApi() {
    const api = (id: string) =>
      HttpService.axios({
        method: "GET",
        url: `/bid_offer/${id}/export/quantities`,
      });
    return HttpService.useRequest<ExportResp>(api, { isOutError: true });
  }

  static useQuantityImportApi() {
    const api = (quoteId: string, url: string) =>
      HttpService.axios({
        method: "POST",
        url: `/bid_offer/import/quantities`,
        data: { url },
      });
    return HttpService.useRequest<QuantityImportResp>(api);
  }

  static useMaterialCreateApi() {
    const api = (quoteId: string, data: MaterialReq) =>
      HttpService.axios({
        method: "POST",
        url: `/bid_offer/${quoteId}/material`,
        data,
      });
    return HttpService.useRequest<void>(api);
  }

  static useMaterialDetailApi() {
    const api = (quoteId: string) =>
      HttpService.axios({
        method: "GET",
        url: `/bid_offer/${quoteId}/material`,
      });
    return HttpService.useRequest<MaterialDetailResp>(api);
  }

  static useMaterialImportApi() {
    const api = (quoteId: string, url: string) =>
      HttpService.axios({
        method: "POST",
        url: `/bid_offer/${quoteId}/import/material`,
        data: { url },
      });
    return HttpService.useRequest<MaterialImportResp>(api);
  }

  static useExportMaterialApi() {
    const api = (id: string) =>
      HttpService.axios({
        method: "GET",
        url: `/bid_offer/${id}/export/material`,
      });
    return HttpService.useRequest<ExportResp>(api, { isOutError: true });
  }

  static detailRespAddUUid(resp: QuoteDetailResp): QuoteReq {
    const req = new QuoteReq();
    const { projectId, offerPrice, responsibleTenantUserId, remark, categorys } = resp;
    const _list = categorys.map((item) => {
      const record = new CategoryRecord();
      item.uuid = generateUuid();
      record.pick(item);
      return record;
    });
    req.merge({
      projectId,
      projectName: resp.projectName,
      offerPrice,
      responsibleTenantUserId,
      remark,
      categorys: _list,
    });
    return req;
  }

  static listAddUUId(list) {
    const _list = list.map((item) => {
      let newItem = _.cloneDeep(item);
      newItem.uuid = generateUuid();
      return newItem;
    });
    return _list;
  }
}
